import { apiClient, bearer, unwrap } from './client';
import type { components } from './types';

export type SupplierBnPeriodOut = components['schemas']['SupplierBnPeriodOut'];
export type SupplierBnPeriodsOut = components['schemas']['SupplierBnPeriodsOut'];
export type SupplierBnPeriodCreateIn = components['schemas']['SupplierBnPeriodCreateIn'];
export type SupplierClassificationUpdateIn = components['schemas']['SupplierClassificationUpdateIn'];
export type SupplierClassificationUpdateOut = components['schemas']['SupplierClassificationUpdateOut'];

/** A supplier's date-ranged BN history, oldest first (INP-508). */
export function listSupplierBnPeriods(
  supplierId: string,
  idToken: string,
): Promise<SupplierBnPeriodsOut> {
  return apiClient
    .GET('/api/suppliers/{supplier_id}/bn-periods', {
      params: { path: { supplier_id: supplierId } },
      headers: bearer(idToken),
    })
    .then(unwrap<SupplierBnPeriodsOut>);
}

/** Add a BN period. An open-ended period (`valid_to` omitted) closes the
 *  prior current one; the API returns 409 on a window/overlap violation. */
export function createSupplierBnPeriod(
  supplierId: string,
  payload: SupplierBnPeriodCreateIn,
  idToken: string,
): Promise<SupplierBnPeriodOut> {
  return apiClient
    .POST('/api/suppliers/{supplier_id}/bn-periods', {
      params: { path: { supplier_id: supplierId } },
      headers: bearer(idToken),
      body: payload,
    })
    .then(unwrap<SupplierBnPeriodOut>);
}

export async function deleteSupplierBnPeriod(
  supplierId: string,
  periodId: string,
  idToken: string,
): Promise<void> {
  await apiClient
    .DELETE('/api/suppliers/{supplier_id}/bn-periods/{period_id}', {
      params: { path: { supplier_id: supplierId, period_id: periodId } },
      headers: bearer(idToken),
    })
    .then(unwrap<void>);
}

/** Set a supplier's tax class, regulatory class, and province (INP-516).
 *  Recorded as a `practitioner` classification override + history row. */
export function updateSupplierClassification(
  supplierId: string,
  payload: SupplierClassificationUpdateIn,
  idToken: string,
): Promise<SupplierClassificationUpdateOut> {
  return apiClient
    .PATCH('/api/suppliers/{supplier_id}/classification', {
      params: { path: { supplier_id: supplierId } },
      headers: bearer(idToken),
      body: payload,
    })
    .then(unwrap<SupplierClassificationUpdateOut>);
}
